import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import DynamicSelect from '../../../DynamicSelect';
import { useAuth } from '../../../../utils/AuthContext';
import { queueOfflineTest } from '../../../../services/pathologyOutbox';
import { saveToCache, readFromCache } from '../../../../services/pathologyCache';

const API_URL = 'https://api.muktihospital.com/api';
const API_KEY = 'caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079';

const AddPathologyTest = ({ initialData, onSuccess, onCancel}) => {
  const { user } = useAuth();
  const [testDetails, setTestDetails] = useState({
    testName: '',
    testCode: '',
    categoryName: '',
    tax: 0,
    standardCharge: 0,
    amount: 0,
    commission: 0,
    performerType: 'doctor',
    performerCommission: 0,
    expense: 0
  });

  const [commType, setCommType] = useState('fixed'); 
  const [commInputVal, setCommInputVal] = useState(0); 
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const isAdmin = user?.role === "admin";
  const isSuperAdmin = user?.role === "superadmin";
  const hasExpensePermission = isAdmin || isSuperAdmin;

  useEffect(() => {
    if (initialData) {
      setTestDetails({
        testName: initialData.testName || '',
        testCode: initialData.testCode || '',
        categoryName: initialData.categoryName || '',
        tax: initialData.tax || 0,
        standardCharge: initialData.standardCharge || 0,
        amount: initialData.amount || 0,
        commission: initialData.commission || 0,
        performerType: initialData.performerType || 'doctor',
        performerCommission: initialData.performerCommission || 0,
        expense: initialData.expense || 0
      });

      setCommType(initialData.commissionType || 'fixed');
      setCommInputVal(initialData.commissionValue || 0);
    }
  }, [initialData]);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await axios.get(`${API_URL}/pathology-category`, {
          headers: { 'x-api-key': API_KEY },
        });
        const list = Array.isArray(response.data.categories) ? response.data.categories : [];
        setCategories(list);
        saveToCache('pathology_categories', list);
      } catch (error) {
        console.error('Error fetching categories:', error);
        if (error.code === 'ERR_NETWORK' || !navigator.onLine) {
          const cached = (await readFromCache('pathology_categories')) || [];
          setCategories(cached);
        } else {
          toast.error('Failed to load categories.');
          setCategories([]);
        }
      }
    };
    fetchCategories();
  }, []);

  // Auto-generate the next numeric test code for NEW tests (skip when editing).
  // Looks at all existing tests, finds the highest numeric code, and adds 1.
  useEffect(() => {
    if (initialData) return; // editing → keep the existing code
    const fetchNextTestCode = async () => {
      try {
        const res = await axios.get(`${API_URL}/pathology`, {
          headers: { 'x-api-key': API_KEY },
        });
        const list = Array.isArray(res.data)
          ? res.data
          : Array.isArray(res.data.data)
          ? res.data.data
          : [];
        let maxCode = 0;
        list.forEach((t) => {
          const n = parseInt(t.testCode, 10);
          if (!isNaN(n) && n > maxCode) maxCode = n;
        });
        const next = maxCode > 0 ? maxCode + 1 : 1;
        setTestDetails((prev) => ({ ...prev, testCode: String(next) }));
      } catch (error) {
        console.error('Error generating next test code:', error);
        // If fetch fails (e.g. offline), leave it empty so user can type manually.
      }
    };
    fetchNextTestCode();
  }, [initialData]);

  const categoryOptions = categories.length > 0
    ? categories.map(category => ({
        value: category.name,
        label: category.name,
      }))
    : [];

  const commissionTypeOptions = [
    { value: 'fixed', label: 'Fixed Amount (৳)' },
    { value: 'percentage', label: 'Percentage (%)' }
  ];

  const performerTypeOptions = [
    { value: 'doctor', label: 'Doctor' },
    { value: 'technician', label: 'Technician' }
  ];

  const recalculateDetails = (updatedDetails, currentCommType, currentCommInput) => {
    const charge = parseFloat(updatedDetails.standardCharge) || 0;
    const vat = parseFloat(updatedDetails.tax) || 0;
    
    const calculatedAmount = charge + (charge * (vat / 100));
    let finalCommission = parseFloat(currentCommInput) || 0;
    if (currentCommType === 'percentage') {
      finalCommission = (charge * (finalCommission / 100));
    }

    return {
      ...updatedDetails,
      amount: isNaN(calculatedAmount) ? 0 : calculatedAmount,
      commission: isNaN(finalCommission) ? 0 : finalCommission,
    };
  };

  const handleTestDetailChange = (field, selectedOption) => {
    const value = selectedOption ? selectedOption.value : '';
    setTestDetails((prevDetails) => {
      const updated = { ...prevDetails, [field]: value };
      return recalculateDetails(updated, commType, commInputVal);
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let newValue = value;

    if (['tax', 'standardCharge', 'commission', 'expense'].includes(name)) {
      newValue = parseFloat(value) || 0;
    }

    setTestDetails((prevDetails) => {
      const updated = { ...prevDetails, [name]: newValue };
      return recalculateDetails(updated, commType, commInputVal);
    });
  };

  const handleCommissionChange = (selectedOption, val) => {
    const type = selectedOption ? selectedOption.value : 'fixed';
    setCommType(type);
    setCommInputVal(val);
    setTestDetails((prevDetails) => {
      return recalculateDetails(prevDetails, type, val);
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (!testDetails.testName || !testDetails.testCode || !testDetails.categoryName) {
      toast.error('Please fill in all required fields (Name, Code, Category).');
      setLoading(false);
      return;
    }

    try {
      const url = initialData
        ? `${API_URL}/pathology/${initialData.id}`
        : `${API_URL}/pathology/add-test`;
      const method = initialData ? 'put' : 'post';

      const payload = {
        ...testDetails,
        commissionType: commType,
        commissionValue: commInputVal,
      };

      const response = await axios[method](url, payload, {
        headers: { 'x-api-key': API_KEY },
      });
      
      const newTest = response.data?.test || response.data;
      if (!newTest || !newTest.id || typeof newTest !== 'object') {
        throw new Error('Invalid test data received from API');
      }
      toast.success(initialData ? 'Test updated successfully' : 'Test added successfully');
      onSuccess(newTest);
    } catch (error) {
      console.error('Error saving test:', error);
      const isNetworkError =
        error.code === 'ERR_NETWORK' || !navigator.onLine;
      if (isNetworkError && !initialData) {
        const clientId =
          typeof crypto !== 'undefined' && crypto.randomUUID
            ? crypto.randomUUID()
            : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
        const payload = {
          ...testDetails,
          commissionType: commType,
          commissionValue: commInputVal,
        };
        const ok = await queueOfflineTest(payload, clientId, {
          userName: user?.name || user?.username || 'Unknown',
        });
        if (ok) {
          toast.success('Test saved offline. It will sync when you are online.');
          onSuccess({
            id: `offline-${clientId}`,
            ...payload,
            __offline: true,
            clientId,
          });
          setLoading(false);
          return;
        }
      }
      toast.error(error.response?.data?.error || (initialData ? 'Failed to update test.' : 'Failed to add test.'));
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setTestDetails({
      testName: '',
      testCode: '',
      categoryName: '',
      tax: 0,
      standardCharge: 0,
      amount: 0,
      commission: 0,
      expense: 0
    });
    setCommType('fixed');
    setCommInputVal(0);
  };

  return (
    <form onSubmit={handleSubmit} className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 px-4 sm:px-0">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-[95%] sm:max-w-[80%] md:max-w-[700px] lg:max-w-[800px] max-h-[90vh] overflow-y-auto">
        <div className="p-4 sm:p-6 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-lg sm:text-xl font-bold text-gray-700">
            {initialData ? 'Edit Test Details' : 'Add Test Details'}
          </h2>
          <button
            type="button"
            className="text-gray-500 hover:text-gray-800 text-lg sm:text-xl"
            onClick={() => {
              resetForm();
              onCancel();
            }}
          >
            ✕
          </button>
        </div>

        <div className="p-4 sm:p-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Test Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Test Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="testName"
              className="w-full p-2 border rounded-md outline-none focus:ring-0 font-inter font-medium text-sm text-gray-700"
              value={testDetails.testName}
              onChange={handleInputChange}
              required
              placeholder="Enter Test Name"
            />
          </div>

          {/* Test Code */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Test Code <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="testCode"
              className="w-full p-2 border rounded-md outline-none focus:ring-0 font-inter font-medium text-sm text-gray-700 uppercase"
              value={testDetails.testCode}
              onChange={handleInputChange}
              required
              placeholder="Auto-generated (you can edit)"
            />
          </div>

          {/* Category Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Category Name <span className="text-red-500">*</span>
            </label>
            <DynamicSelect
              options={categoryOptions}
              onChange={(selectedOption) => handleTestDetailChange('categoryName', selectedOption)}
              value={categoryOptions.find(option => option.value === testDetails.categoryName) || null}
              isSearchable={true}
              placeholder="Select Category"
            />
          </div>

          {/* Vat (%) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Vat (%) <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              name="tax"
              className="w-full p-2 border rounded-md outline-none focus:ring-0 font-inter font-medium text-sm text-gray-700"
              value={testDetails.tax}
              onChange={handleInputChange}
              required
              min="0"
              step="0.01"
            />
          </div>

          {/* Standard Charge */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Standard Charge (৳) <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              name="standardCharge"
              className="w-full p-2 border rounded-md outline-none focus:ring-0 font-inter font-medium text-sm text-gray-700"
              value={testDetails.standardCharge}
              onChange={handleInputChange}
              required
              min="0"
              step="0.01"
            />
          </div>

          {/* Total Amount */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Amount (৳) <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              name="amount"
              className="w-full p-2 border rounded-md outline-none focus:ring-0 font-inter font-medium text-sm text-gray-700 bg-gray-50"
              value={testDetails.amount.toFixed(2)}
              disabled
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Calculated Expense (৳) {!hasExpensePermission && <span className="text-xs text-gray-400 font-normal">(Admin Only)</span>}
            </label>
            <input
              type="number"
              name="expense"
              className={`w-full p-2 border rounded-md outline-none focus:ring-0 font-inter font-medium text-sm ${
                hasExpensePermission 
                  ? "text-gray-700 bg-white border-gray-300 focus:border-M-primary-color" 
                  : "text-gray-400 bg-gray-100 border-gray-200 cursor-not-allowed"
              }`}
              value={testDetails.expense}
              onChange={handleInputChange}
              disabled={!hasExpensePermission}
              placeholder="0.00"
              min="0"
              step="0.01"
            />
          </div>

          {/* Performer Type & Commission (for Expense Posting) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Performer Type (who does this test)
            </label>
            <DynamicSelect
              options={performerTypeOptions}
              onChange={(selectedOption) =>
                setTestDetails((prev) => ({
                  ...prev,
                  performerType: selectedOption ? selectedOption.value : 'doctor',
                }))
              }
              value={
                performerTypeOptions.find((o) => o.value === testDetails.performerType) ||
                performerTypeOptions[0]
              }
              isSearchable={false}
              placeholder="Select Performer Type"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Performer Commission (৳)
            </label>
            <input
              type="number"
              value={testDetails.performerCommission}
              onChange={(e) =>
                setTestDetails((prev) => ({
                  ...prev,
                  performerCommission: parseFloat(e.target.value) || 0,
                }))
              }
              className="w-full p-2 border rounded-md outline-none focus:ring-0 font-inter font-medium text-sm text-gray-700"
              placeholder="Enter ৳"
              min="0"
              step="0.01"
            />
          </div>

          {/* Commission Type Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Commission Type
            </label>
            <DynamicSelect
              options={commissionTypeOptions}
              onChange={(selectedOption) => handleCommissionChange(selectedOption, commInputVal)}
              value={commissionTypeOptions.find(option => option.value === commType) || null}
              isSearchable={false}
              placeholder="Select Commission Type"
            />
          </div>

          {/* Dynamic Commission Value Input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Refer Commission {commType === 'percentage' ? '(%)' : '(৳)'}
            </label>
            <input
              type="number"
              value={commInputVal}
              onChange={(e) => handleCommissionChange({ value: commType }, parseFloat(e.target.value) || 0)}
              className="w-full p-2 border rounded-md outline-none focus:ring-0 font-inter font-medium text-sm text-gray-700"
              placeholder={commType === 'percentage' ? 'Enter %' : 'Enter ৳'}
              min="0"
              step="0.01"
            />
          </div>

          {commType === 'percentage' && (
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">
                Calculated Comm. (৳)
              </label>
              <input
                type="text"
                className="w-full p-2 border rounded-md outline-none font-inter font-medium text-sm text-gray-500 bg-gray-100 italic"
                value={`${testDetails.commission.toFixed(2)} ৳`}
                disabled
              />
            </div>
          )}
        </div>

        <div className="flex flex-col sm:flex-row justify-end gap-3 p-4 sm:p-6 border-t border-gray-200">
          <button
            type="button"
            className="bg-[#E7633D]/90 text-sm text-white py-2 px-6 rounded-md hover:bg-[#E7633D] transition-all duration-300 w-full sm:w-auto"
            onClick={() => {
              resetForm();
              onCancel();
            }}
          >
            {initialData ? 'Cancel' : 'Clear'}
          </button>
          <button
            type="submit"
            className="bg-M-primary-color/90 text-sm text-white py-2 px-6 rounded-md hover:bg-M-primary-color transition-all duration-200 w-full sm:w-auto"
            disabled={loading}
          >
            {loading ? 'Saving...' : initialData ? 'Update' : 'Save'}
          </button>
        </div>
      </div>
    </form>
  );
};

export default AddPathologyTest;